import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { DeveloperProvider, useDeveloper } from '../../contexts/DeveloperContext';
import { hasPermission } from '../../utils/permissions';
import type { TeamRole } from '../../types/company';

interface ProtectedDeveloperRouteProps {
  children: ReactNode;
  requiredRole?: TeamRole;
  redirectTo?: string;
}

function Guard({ children, requiredRole = 'VIEWER', redirectTo = '/signin' }: ProtectedDeveloperRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { developer, myRole, isLoading, error } = useDeveloper();
  const location = useLocation();
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" dir={i18n.dir()}>
        <div className="animate-pulse text-center">
          <div className="h-8 w-48 bg-gray-200 rounded mx-auto mb-4" />
          <div className="h-4 w-32 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to={redirectTo} state={{ from: location }} replace />;

  if (!developer) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" dir={i18n.dir()}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-building-4-line text-2xl text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-2">{isRTL ? 'لا يوجد حساب مطوّر' : 'No developer access'}</h2>
          <p className="text-gray-600 mb-6">{isRTL ? 'تحتاج إلى حساب مطوّر للوصول إلى هذه الصفحة.' : 'You need a developer account to access this page.'}</p>
          <a href="/" className="inline-block px-6 py-2 bg-[#004D40] text-white rounded-lg hover:bg-[#003930] transition-colors">{isRTL ? 'العودة للرئيسية' : 'Back to Home'}</a>
        </div>
      </div>
    );
  }

  if (!hasPermission(myRole, requiredRole)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" dir={i18n.dir()}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-line text-2xl text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-2">{isRTL ? 'صلاحيات غير كافية' : 'Insufficient permissions'}</h2>
          <a href="/developer/dashboard" className="inline-block px-6 py-2 bg-[#004D40] text-white rounded-lg hover:bg-[#003930] transition-colors">{isRTL ? 'لوحة التحكم' : 'Dashboard'}</a>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" dir={i18n.dir()}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2 bg-[#004D40] text-white rounded-lg">{isRTL ? 'إعادة المحاولة' : 'Retry'}</button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function ProtectedDeveloperRoute(props: ProtectedDeveloperRouteProps) {
  return (
    <DeveloperProvider>
      <Guard {...props} />
    </DeveloperProvider>
  );
}
